/* eslint-disable @typescript-eslint/no-explicit-any */
import { debounce } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { usePage } from '../../../contexts/usePageContext';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getBaseDomain } from '../../../redux/pageDomain';
import reduxSlicesList from '../../../redux/reduxSlicesList';
import { RootState } from '../../../redux/store';
import { FormValues, StructureType } from '../../../types/types';
import { mapArrayFormToPatch, mapArrayFormToPatchDelete } from './utils';

const useEditArray = (props: { arrayKey: string }) => {
 const keyCapitalized =
  props.arrayKey.charAt(0).toUpperCase() + props.arrayKey.slice(1);

 const { pageName } = usePage();
 const slice = reduxSlicesList[pageName as keyof typeof reduxSlicesList];
 const dispatch = useAppDispatch();
 const [editingId, setEditingId] = useState<number | null>(null);
 const [showCreateForm, setShowCreateForm] = useState(false);

 const formMethods = useForm<FormValues>();

 const selector = useAppSelector(
  (state: RootState) => state[getBaseDomain(pageName)],
 ) as any;
 const { item, structure, success, loading } = selector;

 const currentItem = item?.[keyCapitalized];

 const isEdit = !!editingId;

 const [changedFields, setChangedFields] = useState<Array<string>>();

 const submitDisabled =
  loading?.createItem ||
  loading?.updateItem ||
  (isEdit ? false : !(changedFields || []).length);

 const currentArrayFieldStructure = useMemo(
  () =>
   structure.find(
    (field: StructureType) =>
     field.Type === 'array' && field.Name === keyCapitalized,
   ),
  [structure, keyCapitalized],
 );

 const fieldsWithData = useMemo(() => {
  if (!structure) return [];

  if (
   !currentArrayFieldStructure ||
   !('Name' in currentArrayFieldStructure) ||
   !item
  )
   return [];

  // removed priority and id field
  const o = [...currentArrayFieldStructure.InnerStructure]
   .filter((field) => field.Priority && field.Name !== 'Id')
   .sort((a, b) => (a?.Priority || 0) - (b?.Priority || 0))
   .map((field) => ({
    ...field,
    Key: field.Name,
    Value: item?.[keyCapitalized]?.find((i: any) => i.Id === editingId)?.[
     field.Name
    ],
    //  Translation: getTranslation(structure, field.Name),
   }));
  return o;
 }, [item, structure, editingId]);

 const handleEdit = (Id: number) => {
  const activeItem = item?.[keyCapitalized]?.find(
   (addr: any) => addr.Id === Id,
  );
  setShowCreateForm(false);

  if (activeItem) {
   formMethods.reset(activeItem);
   setEditingId(Id);
  }
 };

 const handleCancel = () => {
  setEditingId(null);
  setShowCreateForm(false);
  formMethods.reset();
 };

 const handleDelete = (Id: number) => {
  //   dispatch(deleteAddressStart({ table, id, addressId: Id }));
  const submitData = mapArrayFormToPatchDelete(props.arrayKey, Id);
  dispatch(
   slice.updateItemStart({
    table: pageName,
    id: item?.Id || -1,
    data: submitData,
   }),
  );
 };

 const handleCreate = () => {
  setEditingId(null);
  setChangedFields([]);

  // why?
  formMethods.reset(
   Object.keys(formMethods.getValues()).reduce((acc, key) => {
    acc[key] = '';
    return acc;
   }, {} as Record<string, any>),
  );
  setShowCreateForm((prevState) => !prevState);
 };

 const onSubmit = (data: FormValues) => {
  const submitData = mapArrayFormToPatch(
   props.arrayKey,
   editingId || -1,
   data,
   item,
   structure,
   changedFields,
  );
  dispatch(
   slice.updateItemStart({
    table: pageName,
    id: item?.Id || -1,
    data: submitData,
   }),
  );
 };

 useEffect(() => {
  if (isEdit && item) {
   formMethods.reset(item); // this func sets default values
   setChangedFields([]);
  }
 }, [item]);

 useEffect(() => {
  const debouncedHandler = debounce((value, { name, type }) => {
   // console.log('debounce handler', value, name, type);
   if (name) {
    setChangedFields((prev) =>
     prev?.includes(name) ? prev : [...(prev || []), name],
    );
   }
  }, 300);

  const subscription = formMethods.watch(debouncedHandler);

  return () => {
   subscription.unsubscribe();
  };
 }, [formMethods.watch]);

 useEffect(() => {
  if (success?.createItem || success?.updateItem || success?.deleteItem) {
   //  apiRef.current.fetchData();

   dispatch(slice.getItemStart({ table: pageName, id: item?.Id || 0 }));
   setShowCreateForm(false);
  }
 }, [success]);

 return {
  onSubmit,
  formMethods,
  structure,
  loading,
  submitDisabled,
  fieldsWithData,
  currentItem,
  handleEdit,
  showCreateForm,
  setShowCreateForm,
  handleCancel,
  handleDelete,
  editingId,
  setEditingId,
  isEdit,
  handleCreate,
  currentArrayFieldStructure,
 };
};

export default useEditArray;

